
import { db } from './firebase';
import { SavedDocument, WriterConfig, StorageTier, SavedCampaign, DocumentFeedback, TrackedKeyword, RankResult } from '../types';

// Access global firebase instance directly to avoid module resolution issues
const firebase = (window as any).firebase;

// STORAGE PACKAGES CONFIG
export const STORAGE_PACKAGES = {
    BASIC: {
        id: 'basic',
        label: 'Gói Cơ Bản',
        limit: 10,
        price: 0 // Free
    },
    STANDARD: {
        id: 'standard',
        label: 'Gói Tiêu Chuẩn',
        limit: 100,
        price: 25 // Reduced from 50
    },
    EXPERT: {
        id: 'expert',
        label: 'Gói Chuyên Gia',
        limit: 10000, // Unlimited practically
        price: 100 // Reduced from 200
    }
};

export const getStorageLimit = (tier: StorageTier = 'basic'): number => {
    switch (tier) {
        case 'expert': return STORAGE_PACKAGES.EXPERT.limit;
        case 'standard': return STORAGE_PACKAGES.STANDARD.limit;
        default: return STORAGE_PACKAGES.BASIC.limit;
    }
};

export const getPackageName = (tier: StorageTier = 'basic'): string => {
    switch (tier) {
        case 'expert': return STORAGE_PACKAGES.EXPERT.label;
        case 'standard': return STORAGE_PACKAGES.STANDARD.label;
        default: return STORAGE_PACKAGES.BASIC.label;
    }
};

// Purchase Storage Package
export const purchaseStoragePackage = async (userId: string, targetTier: 'standard' | 'expert'): Promise<void> => {
    try {
        const cost = targetTier === 'standard' ? STORAGE_PACKAGES.STANDARD.price : STORAGE_PACKAGES.EXPERT.price;

        await db.runTransaction(async (transaction) => {
            const userRef = db.collection('users').doc(userId);
            const userDoc = await transaction.get(userRef);

            if (!userDoc.exists) throw new Error("User not found");

            const userData = userDoc.data();
            const currentCredits = userData?.credits || 0;
            const currentTier = userData?.storageTier || 'basic';

            // Check if already on higher or equal tier
            if (currentTier === 'expert') {
                 throw new Error("Bạn đang ở gói cao cấp nhất.");
            }
            if (currentTier === 'standard' && targetTier === 'standard') {
                 throw new Error("Bạn đang sử dụng gói này rồi.");
            }

            // Check credits
            if (currentCredits < cost) {
                throw new Error(`Bạn không đủ tín dụng (Cần ${cost}, có ${currentCredits}). Vui lòng nạp thêm.`);
            }

            // Deduct & Upgrade
            transaction.update(userRef, {
                credits: currentCredits - cost,
                storageTier: targetTier
            });
        });
    } catch (error) {
        console.error("Purchase Storage Error:", error);
        throw error;
    }
};

// HELPER: Recursively remove undefined values (Firestore doesn't support them)
const sanitizePayload = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') {
        return obj === undefined ? null : obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(sanitizePayload);
    }
    const result: any = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const val = obj[key];
            result[key] = val === undefined ? null : sanitizePayload(val);
        }
    }
    return result;
};

export const saveDocument = async (
    userId: string, 
    userTier: StorageTier, // Changed from userCredits
    docData: {
        id?: string;
        title: string;
        topic: string;
        content: string;
        config: WriterConfig;
        status?: 'draft' | 'published';
        wpLink?: string;
        isPublic?: boolean;
        shareId?: string | null;
    }
): Promise<string> => {
    try {
        const collectionRef = db.collection('documents');

        // 1. Check Limits if creating NEW document
        // OPTIMIZATION: Skip count check for Expert users to save reads/time
        if (!docData.id && userTier !== 'expert') {
            const snapshot = await collectionRef.where("userId", "==", userId).get();
            const currentCount = snapshot.size;
            const limit = getStorageLimit(userTier);
            
            if (currentCount >= limit) {
                throw new Error(`Bạn đã đạt giới hạn lưu trữ (${limit} bài). Vui lòng nâng cấp gói lưu trữ.`);
            }
        }

        const now = new Date().toISOString();
        const wordCount = docData.content.split(/\s+/).filter(Boolean).length;

        // Use sanitizePayload to ensure no undefined values reach Firestore
        const rawPayload: Partial<SavedDocument> = {
            userId,
            title: docData.title || docData.topic || 'Untitled',
            topic: docData.topic,
            content: docData.content,
            config: docData.config,
            updatedAt: now,
            status: docData.status || 'draft',
            wpLink: docData.wpLink || null, 
            wordCount,
            isPublic: docData.isPublic ?? false,
            shareId: docData.shareId || null
        };

        const payload = sanitizePayload(rawPayload);

        if (docData.id) {
            // Update existing
            const docRef = db.collection('documents').doc(docData.id);
            // Verify ownership
            const existingDoc = await docRef.get();
            if (!existingDoc.exists || existingDoc.data()?.userId !== userId) {
                 throw new Error("Không tìm thấy tài liệu hoặc không có quyền.");
            }
            await docRef.update(payload);
            return docData.id;
        } else {
            // Create new
            payload.createdAt = now;
            const docRef = await collectionRef.add(payload);
            return docRef.id;
        }
    } catch (error) {
        console.error("Save Document Error:", error);
        throw error;
    }
};

export const getUserDocuments = async (userId: string, lastDoc: any = null, pageSize: number = 20): Promise<{ docs: SavedDocument[], lastDoc: any }> => {
    try {
        let query = db.collection('documents')
            .where("userId", "==", userId)
            .orderBy("updatedAt", "desc")
            .limit(pageSize);
        
        if (lastDoc) {
            query = query.startAfter(lastDoc);
        }

        const snapshot = await query.get();
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedDocument));
        
        return {
            docs,
            lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
        };
    } catch (error) {
        console.error("Get Documents Error:", error);
        return { docs: [], lastDoc: null };
    }
};

export const deleteDocument = async (userId: string, docId: string): Promise<void> => {
    try {
        const docRef = db.collection('documents').doc(docId);
        const docSnap = await docRef.get();
        
        if (!docSnap.exists || docSnap.data()?.userId !== userId) {
            throw new Error("Không thể xóa tài liệu này.");
        }

        await docRef.delete();
    } catch (error) {
        console.error("Delete Error:", error);
        throw error;
    }
};

// --- PUBLIC SHARING FEATURE ---

export const togglePublicShare = async (userId: string, docId: string, enable: boolean): Promise<string | null> => {
    try {
        const docRef = db.collection('documents').doc(docId);
        const docSnap = await docRef.get();
        
        if (!docSnap.exists || docSnap.data()?.userId !== userId) {
            throw new Error("Không có quyền chỉnh sửa tài liệu này.");
        }

        if (enable) {
            // Check if already shared
            const existingShareId = docSnap.data()?.shareId;
            if (existingShareId && docSnap.data()?.isPublic) {
                return existingShareId;
            }
            
            // Create new Share ID
            const shareId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            await docRef.update({
                isPublic: true,
                shareId: shareId
            });
            return shareId;
        } else {
            // Disable sharing
            await docRef.update({
                isPublic: false,
                shareId: null // Optional: clear ID or keep it
            });
            return null;
        }
    } catch (error) {
        console.error("Share Toggle Error:", error);
        throw error;
    }
};

export const getPublicDocumentByShareId = async (shareId: string): Promise<SavedDocument | null> => {
    try {
        const snapshot = await db.collection('documents').where('shareId', '==', shareId).limit(1).get();
        
        if (snapshot.empty) return null;
        
        const docData = snapshot.docs[0].data();
        
        if (docData.isPublic) {
            return { 
                id: snapshot.docs[0].id, 
                userId: 'public',
                title: docData.title,
                content: docData.content,
                updatedAt: docData.updatedAt,
                createdAt: docData.createdAt,
                wordCount: docData.wordCount,
                wpLink: docData.wpLink,
                isPublic: true,
                shareId: docData.shareId,
                status: 'published',
                feedbacks: docData.feedbacks || [],
                topic: docData.topic,
                config: {
                    mode: 'create', // Dummy
                    topic: docData.topic,
                    keyword: docData.config?.keyword || '',
                    tone: '',
                    type: '',
                    audience: '',
                    length: '1000',
                    framework: 'none',
                    useEEAT: false,
                    expertContext: '',
                    model: 'gemini-4-argon'
                }
            } as SavedDocument;
        }
        return null;
    } catch (error) {
        console.error("Get Public Doc Error:", error);
        throw error;
    }
};

export const addFeedbackToDocument = async (shareId: string, feedback: Omit<DocumentFeedback, 'id' | 'createdAt'>): Promise<void> => {
    try {
        const snapshot = await db.collection('documents').where('shareId', '==', shareId).limit(1).get();
        if (snapshot.empty) throw new Error("Document not found");
        
        const docRef = snapshot.docs[0].ref;
        const newFeedback: DocumentFeedback = {
            id: Math.random().toString(36).substr(2, 9),
            ...feedback,
            createdAt: new Date().toISOString()
        };

        await docRef.update({
            feedbacks: firebase.firestore.FieldValue.arrayUnion(newFeedback)
        });
        
    } catch (error) {
        console.error("Add Feedback Error:", error);
        throw error;
    }
};

// --- CAMPAIGN STORAGE (AUTO WRITER) ---

export const saveCampaign = async (userId: string, campaign: Omit<SavedCampaign, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        const now = new Date().toISOString();
        const rawPayload = {
            userId,
            ...campaign,
            createdAt: now,
            updatedAt: now
        };
        // Sanitize to handle complex writerConfig or items that might contain undefined
        const payload = sanitizePayload(rawPayload);
        
        const docRef = await db.collection('auto_writer_campaigns').add(payload);
        return docRef.id;
    } catch (error) {
        console.error("Save Campaign Error:", error);
        throw error;
    }
};

export const getUserCampaigns = async (userId: string): Promise<SavedCampaign[]> => {
    try {
        const snapshot = await db.collection('auto_writer_campaigns').where('userId', '==', userId).get();
        const campaigns = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedCampaign));
        return campaigns.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } catch (error) {
        console.error("Get Campaigns Error:", error);
        return [];
    }
};

export const deleteCampaign = async (userId: string, campaignId: string): Promise<void> => {
    try {
        const docRef = db.collection('auto_writer_campaigns').doc(campaignId);
        const docSnap = await docRef.get();
        if (!docSnap.exists || docSnap.data()?.userId !== userId) throw new Error("Không có quyền.");
        await docRef.delete();
    } catch (error) {
        console.error("Delete Campaign Error:", error);
        throw error;
    }
};

// --- RANK TRACKING STORAGE ---

export const saveRankResult = async (userId: string, domain: string, location: string, result: RankResult): Promise<void> => {
    try {
        const collectionRef = db.collection('keyword_rankings');
        
        const snapshot = await collectionRef
            .where('userId', '==', userId)
            .where('domain', '==', domain)
            .where('keyword', '==', result.keyword)
            .where('location', '==', location)
            .limit(1)
            .get();

        const today = new Date().toISOString();
        const historyPoint = { date: today, rank: result.rank };

        if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            await doc.ref.update({
                currentRank: result.rank,
                lastChecked: today,
                url: result.url || doc.data().url,
                history: firebase.firestore.FieldValue.arrayUnion(historyPoint)
            });
        } else {
            const newRecord: TrackedKeyword = {
                userId,
                domain,
                keyword: result.keyword,
                location,
                currentRank: result.rank,
                url: result.url,
                volume: result.volume,
                lastChecked: today,
                history: [historyPoint]
            };
            await collectionRef.add(newRecord);
        }
    } catch (error) {
        console.error("Save Rank Error:", error);
        throw error;
    }
};

export const getUserRankings = async (userId: string): Promise<TrackedKeyword[]> => {
    try {
        const snapshot = await db.collection('keyword_rankings').where('userId', '==', userId).get();
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TrackedKeyword));
        return data.sort((a, b) => new Date(b.lastChecked).getTime() - new Date(a.lastChecked).getTime());
    } catch (error) {
        console.error("Get Rankings Error:", error);
        return [];
    }
};

export const deleteTrackedKeyword = async (userId: string, id: string): Promise<void> => {
    try {
        const docRef = db.collection('keyword_rankings').doc(id);
        const doc = await docRef.get();
        if (!doc.exists || doc.data()?.userId !== userId) throw new Error("Permission denied");
        await docRef.delete();
    } catch (error) {
        console.error("Delete Ranking Error:", error);
        throw error;
    }
};
